import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type FloatingMenuProps = {
  anchorRef: React.RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  placement?: "top" | "bottom";
  children: React.ReactNode;
};

/**
 * Renders its children in a portal appended to document.body, positioned
 * next to `anchorRef`. Escapes any ancestor with overflow:hidden/auto so it
 * never gets clipped or forces a scrollbar on a scrolling container (e.g.
 * a floating panel footer).
 *
 * Anchors via `bottom` (placement="top") or `top` (placement="bottom")
 * instead of computing `top = anchorTop - menuHeight`, so the menu never
 * overlaps the anchor even before its real height is known (e.g. while
 * content is still loading and grows taller afterwards).
 */
export default function FloatingMenu({
  anchorRef,
  open,
  onClose,
  placement = "top",
  children
}: FloatingMenuProps) {
  const portalRootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<React.CSSProperties>({
    visibility: "hidden"
  });

  useEffect(() => {
    const el = document.createElement("div");
    portalRootRef.current = el;
    document.body.appendChild(el);
    return () => {
      if (portalRootRef.current) {
        document.body.removeChild(portalRootRef.current);
      }
    };
  }, []);

  // cerrar al click fuera o ESC
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (portalRootRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current || !portalRootRef.current) return;
    const gap = 6;
    const place = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      const menuWidth = menuRef.current?.offsetWidth ?? 0;
      let left = rect.left;
      const maxLeft = window.innerWidth - menuWidth - 8;
      if (left > maxLeft) left = Math.max(8, maxLeft);
      setStyle(
        placement === "top"
          ? {
              position: "fixed",
              bottom: window.innerHeight - rect.top + gap,
              left,
              zIndex: 2147483647,
              visibility: "visible"
            }
          : {
              position: "fixed",
              top: rect.bottom + gap,
              left,
              zIndex: 2147483647,
              visibility: "visible"
            }
      );
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(document.body);
    if (menuRef.current) ro.observe(menuRef.current);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, anchorRef, placement, children]);

  if (!open || !portalRootRef.current) return null;

  return createPortal(
    <div ref={menuRef} style={style} role="menu">
      {children}
    </div>,
    portalRootRef.current
  );
}
